if (!customElements.get('cart-accessory-offer')) {
  class CartAccessoryOffer extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick || ((event) => {
        const add = event.target.closest('[data-add]');
        const addWithMessage = event.target.closest('[data-add-with-message]');
        const cancel = event.target.closest('[data-cancel-message]');
        const control = add || addWithMessage || cancel;
        const row = control && control.closest('[data-row]');
        if (!row) return;

        if (add) this.onAddClick(row);
        else if (addWithMessage) this.onAddWithMessage(row);
        else this.onCancel(row);
      });
      this.addEventListener('click', this.onClick);
      this.onKeydown = this.onKeydown || ((event) => {
        if (event.key !== 'Escape') return;
        const panel = event.target.closest('[data-message-panel]');
        if (!panel || panel.hidden) return;
        const row = panel.closest('[data-row]');
        if (!row) return;
        event.preventDefault();
        this.onCancel(row);
      });
      this.addEventListener('keydown', this.onKeydown);

      // This survives innerHTML swaps because the live region is outside the element.
      this.live = this.parentElement.querySelector('[data-cart-accessory-offer-live]');
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
    }

    disconnectedCallback() { if (this.unsubscribe) this.unsubscribe(); }
    get sectionId() { return this.dataset.sectionId; }
    get cartItems() { return document.querySelector('cart-items') || null; }
    inheritedTargets() {
      try {
        return this.cartItems
          ? this.cartItems.getSectionsToRender().filter((target) => target.id !== 'cart-live-region-text')
          : [];
      } catch (e) {
        return [];
      }
    }
    sectionsToRequest() {
      return [this.sectionId, ...this.inheritedTargets().map((target) => target.section)].slice(0, 5);
    }
    titleOf(row) { return ((row.querySelector('.cart-accessory-offer__title') || {}).textContent || '').trim(); }
    rowFor(variantId) { return this.querySelector('[data-variant-id="' + variantId + '"]'); }
    onAddClick(row) {
      if (row.classList.contains('is-added')) return;
      if (row.hasAttribute('data-requires-message')) {
        const add = row.querySelector('[data-add]');
        const panel = row.querySelector('[data-message-panel]');
        const textarea = panel && panel.querySelector('[data-card-message]');
        if (!add || !panel || !textarea) return;
        add.hidden = true;
        panel.hidden = false;
        textarea.focus();
        return;
      }
      this.add(row, null);
    }
    onAddWithMessage(row) {
      const field = row.querySelector('card-message-field');
      if (!field || typeof field.validate !== 'function') return;
      const message = field.validate();
      if (message === null) return;
      this.add(row, message);
    }
    onCancel(row) {
      const add = row.querySelector('[data-add]');
      const panel = row.querySelector('[data-message-panel]');
      if (!add || !panel) return;
      panel.hidden = true;
      this.hideError(row);
      add.hidden = false;
      add.focus();
    }
    async add(row, message) {
      if (this.dataset.busy || row.classList.contains('is-added')) return;
      this.dataset.busy = '1';
      const variantId = row.dataset.variantId;
      const title = this.titleOf(row);
      this.hideError(row);
      this.setLoading(row, true);

      const item = { id: Number(variantId), quantity: 1 };
      if (message) item.properties = { 'Card message': message };
      const body = { items: [item], sections: this.sectionsToRequest(), sections_url: window.location.pathname };
      const config = fetchConfig('json');
      config.body = JSON.stringify(body);
      try {
        const res = await fetch(routes.cart_add_url, config);
        let data = null;
        try { data = await res.json(); } catch (e) { data = null; }
        const addedItems = Array.isArray(data && data.items) ? data.items : (data && data.variant_id ? [data] : []);
        const addedConfirmed = !!(res.ok && addedItems.some((line) => String(line.variant_id) === String(variantId)));
        // Only server-rendered markup may confirm that the accessory is in the cart.
        const ok = !!(
          res.ok &&
          data &&
          !data.status &&
          !data.errors &&
          data.sections &&
          typeof data.sections[this.sectionId] === 'string'
        );
        if (ok && this.render(data.sections)) {
          this.announce(title + ' added to your cart.');
          const added = this.rowFor(variantId);
          if (added) added.focus({ preventScroll: true });
          return;
        }
        // A successful add with an incomplete sections response needs confirmation.
        if (res.ok) this.renderInherited(data && data.sections);
        const refreshed = res.ok ? await this.refresh() : false;
        const target = this.rowFor(variantId); // Re-find after the possible refreshed markup swap.
        if (target && target.classList.contains('is-added')) {
          this.announce(title + ' added to your cart.');
          target.focus({ preventScroll: true });
          return;
        } else if (target && addedConfirmed && !refreshed) {
          // Shopify's add response is authoritative only when our section could not be
          // re-rendered (refresh failed); a successful refresh is fresher truth and wins.
          this.markAdded(target);
          this.announce(title + ' added to your cart.');
          target.focus({ preventScroll: true });
          return;
        }
        if (target) {
          this.setLoading(target, false);
          this.showError(target, "Couldn't add " + title + '. Please try again.');
          const control = target.querySelector('[data-add-with-message]:not([hidden]), [data-add]:not([hidden])');
          if (control) control.focus({ preventScroll: true });
        }
      } catch (e) {
        const target = this.rowFor(variantId);
        if (target) {
          this.setLoading(target, false);
          this.showError(target, "Couldn't add " + title + '. Please try again.');
        }
      } finally {
        delete this.dataset.busy;
      }
    }
    markAdded(row) {
      row.classList.add('is-added');
      this.hideError(row);
      this.setLoading(row, false);
      const add = row.querySelector('[data-add]');
      const panel = row.querySelector('[data-message-panel]');
      if (add) add.remove();
      if (panel) panel.remove();
      const added = document.createElement('span');
      added.className = 'cart-accessory-offer__added';
      added.textContent = 'Added · in your cart';
      const info = row.querySelector('.cart-accessory-offer__info');
      if (info) info.insertAdjacentElement('afterend', added);
    }
    setLoading(row, on) {
      row.querySelectorAll('[data-add], [data-add-with-message]').forEach((button) => {
        const spinner = button.querySelector('.loading__spinner');
        if (on) {
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');
        } else {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      });
    }
    showError(row, text) {
      const error = row.querySelector('[data-error]');
      if (!error) return;
      error.textContent = text; error.hidden = false;
    }
    hideError(row) {
      const error = row.querySelector('[data-error]');
      if (!error) return;
      error.textContent = ''; error.hidden = true;
    }
    renderInherited(sections) {
      if (!sections || typeof sections !== 'object' || !this.cartItems) return;
      for (const target of this.inheritedTargets()) {
        const element = document.getElementById(target.id);
        if (!element || typeof sections[target.section] !== 'string') continue;
        try {
          (element.querySelector(target.selector) || element).innerHTML =
            this.cartItems.getSectionInnerHTML(sections[target.section], target.selector);
        } catch (e) {
          console.warn('[cart-accessory-offer] section swap failed', target.id, e);
        }
      }
    }
    capturePanel() {
      const openPanel = this.querySelector('[data-message-panel]:not([hidden])');
      const openRow = openPanel && openPanel.closest('[data-row]');
      const openTextarea = openPanel && openPanel.querySelector('[data-card-message]');
      return openRow && openTextarea && openTextarea.value
        ? { variantId: openRow.dataset.variantId, text: openTextarea.value }
        : null;
    }
    restorePanel(captured) {
      if (!captured) return;
      const row = this.rowFor(captured.variantId);
      const panel = row && row.querySelector('[data-message-panel]');
      const textarea = panel && panel.querySelector('[data-card-message]');
      if (!row || row.classList.contains('is-added') || !panel || !textarea) return;
      const add = row.querySelector('[data-add]');
      if (add) add.hidden = true;
      panel.hidden = false;
      textarea.value = captured.text;
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
    render(sections) {
      const parsed = new DOMParser().parseFromString(sections[this.sectionId], 'text/html');
      const root = parsed.querySelector('cart-accessory-offer');
      if (!root) return false;
      const captured = this.capturePanel();
      this.innerHTML = root.innerHTML;
      this.restorePanel(captured);
      this.renderInherited(sections);
      return true;
    }
    refresh() {
      this._refreshResolvers = this._refreshResolvers || [];
      clearTimeout(this._refreshTimer);
      return new Promise((resolve) => {
        this._refreshResolvers.push(resolve);
        this._refreshTimer = setTimeout(() => {
          const waiters = this._refreshResolvers;
          this._refreshResolvers = [];
          const settle = (result) => waiters.forEach((resolver) => resolver(result));
          this._doRefresh().then((result) => settle(result), () => settle(false));
        }, 250);
      });
    }
    async _doRefresh() {
      const captured = this.capturePanel();
      try {
        const res = await fetch(routes.cart_url + '?section_id=' + encodeURIComponent(this.sectionId));
        if (!res.ok) return false;
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const root = parsed.querySelector('cart-accessory-offer');
        const wrapper = this.closest('.cart-accessory-offer');
        if (root) {
          this.innerHTML = root.innerHTML;
          if (wrapper) wrapper.hidden = false;
          this.restorePanel(captured);
          return true;
        } else {
          this.innerHTML = '';
          if (wrapper) wrapper.hidden = true;
          if (this.live) this.live.textContent = '';
          return false;
        }
      } catch (e) {
        // Leave the current DOM intact on refresh failure.
        return false;
      }
    }
    announce(text) {
      if (!this.live) return;
      this.live.textContent = '';
      requestAnimationFrame(() => {
        this.live.textContent = text;
      });
    }
  }
  customElements.define('cart-accessory-offer', CartAccessoryOffer);
}
